import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button, cn } from "@nexus/ui";
import {
  useVersionAnnouncement,
  type VersionNoticeMode,
} from "@/hooks/useVersionAnnouncement";
import {
  countReleaseNoteItems,
  limitReleaseNoteItems,
  parseReleaseNotes,
} from "@/lib/release-notes";

const VERSION_BANNER_DISMISS_PREFIX = "nexus_version_banner_dismissed:";

/** Enough to convey what shipped; the rest is one click away. */
const COLLAPSED_NOTE_COUNT = 3;

/** Enter/exit motion — keep in sync with Tailwind `duration-150`. */
export const VERSION_BANNER_ENTER_MS = 150;
export const VERSION_BANNER_EXIT_MS = 150;

/** Brief acknowledge delay before reload so the Refresh click feels intentional. */
export const VERSION_BANNER_REFRESH_ACK_MS = 280;

/**
 * How long the spinner may hold after the reload is triggered. A committed
 * reload unmounts this component well inside the window, so the reset never
 * fires in the normal case. It exists for the stalled one: `index.html` is
 * `no-store`, so a navigation routed to a draining app pod can block for tens
 * of seconds, and without a reset the only action on the banner stays disabled
 * for as long as that takes.
 */
export const VERSION_BANNER_REFRESH_TIMEOUT_MS = 8_000;

function dismissStorageKey(sha: string): string {
  return `${VERSION_BANNER_DISMISS_PREFIX}${sha}`;
}

function readDismissed(sha: string): boolean {
  try {
    return sessionStorage.getItem(dismissStorageKey(sha)) === "true";
  } catch {
    return false;
  }
}

function writeDismissed(sha: string): void {
  try {
    sessionStorage.setItem(dismissStorageKey(sha), "true");
  } catch {
    /* private browsing — in-memory dismiss still applies */
  }
}

export interface VersionUpdateBannerViewProps {
  onRefresh: () => void;
  onDismiss: () => void;
  mode?: VersionNoticeMode;
  releaseNotes?: string | null;
  isExiting?: boolean;
  isRefreshing?: boolean;
}

/**
 * Quiet version notice — Linear-style edge strip, not a marketing toast.
 * Distinct from the offline ink pill so the two states never look alike.
 *
 * In `notice` mode the client already runs the newest bundle, so reloading
 * would change nothing — the only action offered is acknowledgement.
 */
export function VersionUpdateBannerView({
  onRefresh,
  onDismiss,
  mode = "refresh",
  releaseNotes = null,
  isExiting = false,
  isRefreshing = false,
}: VersionUpdateBannerViewProps) {
  const { t } = useTranslation();
  const isRefreshMode = mode === "refresh";

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        "fixed z-50 w-[min(calc(100%-1.5rem),20rem)] overflow-hidden rounded-sm border border-border bg-background",
        "bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2",
        "sm:left-auto sm:right-4 sm:translate-x-0",
        isExiting
          ? "motion-safe:animate-out motion-safe:fade-out motion-safe:duration-150 motion-safe:ease-in motion-reduce:animate-none"
          : "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-150 motion-safe:ease-out motion-reduce:animate-none",
      )}
    >
      {/* Functional accent — marks “new”, not a decorative icon well */}
      <div className="flex border-l-2 border-l-primary">
        <div className="flex min-w-0 flex-1 flex-col gap-2 px-3 py-2.5">
          <p className="text-[13px] font-medium leading-snug tracking-tight text-foreground">
            {isRefreshMode
              ? t("admin.versionBanner.headline", "Update available")
              : t("admin.versionBanner.noticeHeadline", "What's new")}
            <span className="font-normal text-muted-foreground">
              {" "}
              {isRefreshMode
                ? t(
                    "admin.versionBanner.description",
                    "Refresh when ready — you can keep working.",
                  )
                : t(
                    "admin.versionBanner.noticeDescription",
                    "You're already on the latest version.",
                  )}
            </span>
          </p>

          <ReleaseNotes notes={releaseNotes} />

          <div className="flex items-center gap-1">
            {isRefreshMode ? (
              <>
                <Button
                  type="button"
                  size="xs"
                  onClick={onRefresh}
                  disabled={isRefreshing || isExiting}
                  aria-busy={isRefreshing}
                  className="h-7 min-w-[4.5rem] cursor-pointer px-2.5"
                >
                  {isRefreshing ? (
                    <Loader2
                      className="size-3 motion-safe:animate-spin motion-reduce:animate-none"
                      aria-hidden
                    />
                  ) : null}
                  {t("admin.versionBanner.refresh", "Refresh")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={onDismiss}
                  disabled={isRefreshing || isExiting}
                  className="h-7 cursor-pointer px-2 text-muted-foreground"
                >
                  {t("common.dismiss", "Dismiss")}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                size="xs"
                onClick={onDismiss}
                disabled={isExiting}
                className="h-7 cursor-pointer px-2.5"
              >
                {t("admin.versionBanner.gotIt", "Got it")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Notes arrive as free text and read as one grey block if printed verbatim.
 * Sections, bullets and a bolded feature name give the eye somewhere to land,
 * and collapsing past the third item keeps the card the size of a notice
 * rather than a changelog page.
 */
function ReleaseNotes({ notes }: { notes: string | null }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const sections = useMemo(() => parseReleaseNotes(notes), [notes]);

  if (sections.length === 0) return null;

  const total = countReleaseNoteItems(sections);
  const hasOverflow = total > COLLAPSED_NOTE_COUNT;
  const visible =
    hasOverflow && !isExpanded
      ? limitReleaseNoteItems(sections, COLLAPSED_NOTE_COUNT)
      : sections;

  return (
    <div className="flex flex-col gap-2">
      {visible.map((section, index) => (
        <div key={section.heading ?? index} className="flex flex-col gap-1">
          {section.heading ? (
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
              {section.heading}
            </p>
          ) : null}
          <ul className="flex flex-col gap-1">
            {section.items.map((item) => (
              <li
                key={item.text}
                className="flex gap-1.5 text-xs leading-relaxed text-muted-foreground"
              >
                <span
                  aria-hidden
                  className="mt-[0.45rem] size-1 shrink-0 rounded-full bg-muted-foreground/40"
                />
                <span className="min-w-0">
                  {item.label ? (
                    <span className="font-medium text-foreground">{item.label}: </span>
                  ) : null}
                  {item.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {hasOverflow ? (
        <button
          type="button"
          onClick={() => setIsExpanded((previous) => !previous)}
          className="cursor-pointer self-start text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
        >
          {isExpanded
            ? t("admin.versionBanner.showLess", "Show less")
            : t("admin.versionBanner.showAll", "Show all {{count}}", { count: total })}
        </button>
      ) : null}
    </div>
  );
}

export interface VersionUpdateIndicatorProps {
  onOpen: () => void;
}

/** Quiet rediscoverable control after dismiss — text chip, not a badge-dot FAB. */
export function VersionUpdateIndicator({ onOpen }: VersionUpdateIndicatorProps) {
  const { t } = useTranslation();

  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      onClick={onOpen}
      aria-label={t("admin.versionBanner.reopen", "Update available — show prompt")}
      className={cn(
        "fixed z-50 h-7 cursor-pointer rounded-sm border-border bg-background px-2.5",
        "bottom-[max(1rem,env(safe-area-inset-bottom))] right-4",
        "text-muted-foreground hover:text-foreground",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150 motion-safe:ease-out motion-reduce:animate-none",
      )}
    >
      {t("admin.versionBanner.indicator", "Update")}
    </Button>
  );
}

export function VersionUpdateBanner() {
  // Keyed on the served SHA, not the API's: the prompt is an offer to pick up
  // the bundle this tier now hands out, so a dismissal belongs to that bundle.
  const { shouldShow, mode, refresh, servedSha, releaseNotes, acknowledge } =
    useVersionAnnouncement();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (refreshResetTimerRef.current) {
      clearTimeout(refreshResetTimerRef.current);
      refreshResetTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!servedSha) {
      setIsDismissed(false);
      setIsExiting(false);
      return;
    }
    setIsDismissed(readDismissed(servedSha));
    setIsExiting(false);
  }, [servedSha]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  // Acknowledging on every exit path keeps a reload from replaying the same
  // announcement as a `notice` once the SHA mismatch is gone.
  const handleDismiss = useCallback(() => {
    if (isExiting || isRefreshing) return;
    setIsExiting(true);
    exitTimerRef.current = setTimeout(() => {
      if (servedSha && mode === "refresh") writeDismissed(servedSha);
      acknowledge();
      setIsDismissed(true);
      setIsExiting(false);
      exitTimerRef.current = null;
    }, VERSION_BANNER_EXIT_MS);
  }, [acknowledge, isExiting, isRefreshing, mode, servedSha]);

  const handleRefresh = useCallback(() => {
    if (isRefreshing || isExiting) return;
    setIsRefreshing(true);
    acknowledge();
    refreshTimerRef.current = setTimeout(() => {
      refresh();
      refreshTimerRef.current = null;
    }, VERSION_BANNER_REFRESH_ACK_MS);
    refreshResetTimerRef.current = setTimeout(() => {
      setIsRefreshing(false);
      refreshResetTimerRef.current = null;
    }, VERSION_BANNER_REFRESH_ACK_MS + VERSION_BANNER_REFRESH_TIMEOUT_MS);
  }, [acknowledge, isExiting, isRefreshing, refresh]);

  const handleReopen = useCallback(() => {
    setIsDismissed(false);
    setIsExiting(false);
  }, []);

  if (!shouldShow) return null;

  // The per-SHA dismissal only mutes the reload prompt. A later announcement on
  // that same SHA is a new signal and must not inherit that dismissal.
  if (isDismissed && mode === "refresh") {
    return <VersionUpdateIndicator onOpen={handleReopen} />;
  }

  return (
    <VersionUpdateBannerView
      onRefresh={handleRefresh}
      onDismiss={handleDismiss}
      mode={mode}
      releaseNotes={releaseNotes}
      isExiting={isExiting}
      isRefreshing={isRefreshing}
    />
  );
}
